import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import type { LDClient, LDContext, LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { basicLogger, initialize } from 'launchdarkly-js-client-sdk';

import { environment, isDrivenByE2e } from '@ninety/mobile/platform/environments';
import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { FeatureFlagActions } from '../feature-flag-state.actions';
import { FeatureFlagContext, FeatureFlagStateModel, initialFeatureFlagState } from '../feature-flag-state.model';

/** The single unidentified context LD boots with and is re-pointed to on sign-out. */
export const ANONYMOUS_LD_CONTEXT: LDSingleKindContext = {
  kind: 'user',
  name: 'anonymous',
  key: 'anonymous',
  anonymous: true,
};

@Injectable({ providedIn: 'root' })
export class LaunchDarklyService {
  private readonly store = inject(Store);
  private readonly logs = inject(DatadogLogsService);

  ldClient: LDClient | null = null;
  ldUserContext: LDContext | null = null;

  // Serialize context updates: the SDK has no single-flight guard on identify(), so overlapping
  // calls on different identities (re-anonymize vs. login) would let the last fetch to resolve win.
  private contextUpdates: Promise<unknown> = Promise.resolve();
  private latestContextRequest = 0;

  init(context: LDSingleKindContext): void {
    if (this.ldUserContext !== null) {
      return;
    }

    this.ldUserContext = context;

    // No client under the end-to-end suite, so the page makes no request to the flag service and a
    // run cannot depend on what it serves that day. Values come from the `feature-flags` cookie,
    // which the reducer applies over whatever this dispatches.
    if (isDrivenByE2e()) {
      const flagContext = this.flagContext(context);
      this.store.dispatch(
        FeatureFlagActions.initialFlagsReceived({ flags: initialFeatureFlagState, context: flagContext })
      );
      return;
    }

    this.ldClient = initialize(environment.launchDarklySdkKey, context, {
      logger: basicLogger({ level: 'warn' }),
    });

    this.ldClient.on('ready', () => {
      if (!this.ldClient) return;
      const flags = this.ldClient.allFlags() as FeatureFlagStateModel;
      this.store.dispatch(FeatureFlagActions.initialFlagsReceived({ flags, context: this.currentFlagContext() }));
    });

    this.ldClient.on('change', () => {
      if (!this.ldClient) return;
      const newFlags = this.ldClient.allFlags() as FeatureFlagStateModel;
      this.store.dispatch(FeatureFlagActions.updatedFlagsReceived({ newFlags, context: this.currentFlagContext() }));
    });
  }

  updateContext(updatedUserContext: LDSingleKindContext): Promise<void> {
    const request = ++this.latestContextRequest;
    const run = this.contextUpdates.then(() => this.identify(updatedUserContext, request));
    this.contextUpdates = run.catch(() => undefined); // keep the queue alive regardless of outcome
    return run;
  }

  private async identify(updatedUserContext: LDSingleKindContext, request: number): Promise<void> {
    // A newer updateContext superseded this one; skip rather than adopt a soon-overwritten context.
    if (request !== this.latestContextRequest) return;

    const flagContext = this.flagContext(updatedUserContext);

    // Keeps the chain the readiness flag rides on: `updatedFlagsReceived` reaches the reducer through
    // `processFlagUpdates`, which is the only path that sets `userFlagsAreLoaded` — and a route guard
    // waits on it. Returning early without dispatching would hang that guard forever.
    if (isDrivenByE2e()) {
      this.store.dispatch(
        FeatureFlagActions.updatedFlagsReceived({ newFlags: initialFeatureFlagState, context: flagContext })
      );
      return;
    }

    // Null client = init never ran; log it, else a guard waiting on the flag chain hangs silently.
    if (!this.ldClient) {
      this.logs.error('LaunchDarkly updateContext called before init; no client', { context: flagContext });
      return;
    }
    await this.ldClient.identify(updatedUserContext).then(() => {
      const newFlags = this.ldClient?.allFlags() as FeatureFlagStateModel;
      // Assign here, not before identify: the client only adopts the context once its fetch succeeds.
      this.ldUserContext = this.ldClient?.getContext() ?? updatedUserContext;
      this.store.dispatch(FeatureFlagActions.updatedFlagsReceived({ newFlags, context: flagContext }));
    });
  }

  /** Read the live client because a rejected `identify()` never adopts the requested context. */
  private currentFlagContext(): FeatureFlagContext {
    const context = this.ldClient?.getContext() as LDSingleKindContext | undefined;
    return this.flagContext(context);
  }

  private flagContext(context: LDSingleKindContext | undefined): FeatureFlagContext {
    return context && context.anonymous !== true ? 'identified' : 'anonymous';
  }

  closeConnection(): void {
    if (!this.ldClient) return;
    this.ldClient.close();
  }
}
